import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  Keyboard,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import type { ActionSheetRef } from "react-native-actions-sheet";
import { AppText } from "@/components/ui/AppText";
import { Icon } from "@/components/ui/Icon";
import { Label } from "@/components/ui/Label";
import { SearchInput } from "@/components/ui/SearchInput";
import { EmptyState } from "@/components/ui/EmptyState";
import { CountryPickerSheet } from "@/components/sheets/CountryPickerSheet";
import type { CountryOption } from "@/components/ui/PhoneInput";
import { WORLD_COUNTRIES } from "@/constants/countries";
import { useI18n } from "@/context/I18nContext";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { cn } from "@/lib/utils";

export interface CountryPickerProps {
  countryName: string;
  countryFlag?: string;
  editable?: boolean;
  onCountrySelect?: (country: CountryOption) => void;
  testID?: string;
  /** Optional label rendered above the picker. */
  label?: TranslationKeys;
  /** When true, renders a red asterisk after the label. */
  required?: boolean;
  hint?: TranslationKeys;
  error?: TranslationKeys;
  containerClassName?: string;
  /** Placeholder translation key shown when no country is selected. Defaults to common.selectOption. */
  placeholder?: TranslationKeys;
}

export const CountryPicker = memo(function CountryPicker({
  countryName,
  countryFlag,
  editable = true,
  onCountrySelect,
  testID,
  label,
  hint,
  error,
  required,
  containerClassName,
  placeholder = "common.selectOption",
}: CountryPickerProps) {
  const { t } = useI18n();
  const isWide = useIsWideScreen();
  const { height: windowHeight } = useWindowDimensions();
  const actionSheetRef = useRef<ActionSheetRef>(null);
  const triggerRef = useRef<View>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{
    triggerTop: number;
    triggerBottom: number;
    left: number;
    width: number;
  } | null>(null);
  const [keyboardTop, setKeyboardTop] = useState<number | null>(null);

  useEffect(() => {
    if (!isWide) {
      setDropdownOpen(false);
      setDropdownPos(null);
      return;
    }
  }, [isWide]);

  useEffect(() => {
    if (Platform.OS === "web" || !isWide) return;
    const show = Keyboard.addListener("keyboardDidShow", (e) => {
      setKeyboardTop(e.endCoordinates.screenY);
      triggerRef.current?.measureInWindow((x, y, w, h) => {
        if (dropdownOpen)
          setDropdownPos({ triggerTop: y, triggerBottom: y + h, left: x, width: w });
      });
    });
    const hide = Keyboard.addListener("keyboardDidHide", () => {
      setKeyboardTop(null);
      triggerRef.current?.measureInWindow((x, y, w, h) => {
        if (dropdownOpen)
          setDropdownPos({ triggerTop: y, triggerBottom: y + h, left: x, width: w });
      });
    });
    return () => {
      show.remove();
      hide.remove();
    };
  }, [isWide, dropdownOpen]);

  const borderClass = error ? "border-error" : editable ? "border-stroke-50" : "border-stroke-20";
  const bgClass = editable ? "bg-white" : "bg-stroke-20";
  const textClass = editable ? "text-dark-text-50" : "text-dark-text-15";

  const selected: CountryOption | undefined = countryName
    ? { flag: countryFlag ?? "", name: countryName, dialCode: "" }
    : undefined;

  const filteredCountries = useMemo(() => {
    if (!searchQuery.trim()) return WORLD_COUNTRIES;
    const lower = searchQuery.toLowerCase();
    return WORLD_COUNTRIES.filter((c) => c.name.toLowerCase().includes(lower));
  }, [searchQuery]);

  const open = () => {
    if (!editable) return;
    setSearchQuery("");
    if (isWide) {
      triggerRef.current?.measureInWindow((x, y, w, h) => {
        setDropdownPos({ triggerTop: y, triggerBottom: y + h, left: x, width: w });
        setDropdownOpen(true);
      });
    } else {
      actionSheetRef.current?.show();
    }
  };

  const close = () => {
    actionSheetRef.current?.hide();
  };

  // ─── Tablet panel placement ────────────────────────────────────────────────
  const _effectiveBottom = keyboardTop ?? windowHeight;
  const _spaceBelow = _effectiveBottom - (dropdownPos?.triggerBottom ?? 0) - 16;
  const _spaceAbove = (dropdownPos?.triggerTop ?? 0) - 16;
  const _flipUp = _spaceBelow < 120 && _spaceAbove > _spaceBelow;
  const _panelMaxH = Math.min(400, Math.max(0, _flipUp ? _spaceAbove : _spaceBelow));

  return (
    <View className={cn("mb-4 w-full items-start", containerClassName)}>
      {!!label && <Label label={label} required={required} />}
      <View ref={triggerRef} className="w-full">
        <TouchableOpacity
          onPress={open}
          disabled={!editable}
          activeOpacity={0.7}
          accessibilityRole="combobox"
          accessibilityLabel={
            countryName ? t("countryPicker.a11y.country", { name: countryName }) : t(placeholder)
          }
          accessibilityState={{ disabled: !editable, expanded: isWide ? dropdownOpen : undefined }}
          testID={testID}
          className={`flex-row justify-between items-center gap-1.5 py-3 px-3 rounded-md border ${borderClass} ${bgClass}`}
        >
          <View className="flex-row items-center gap-1.5">
            {countryFlag ? <AppText className="text-lg">{countryFlag}</AppText> : null}
            {countryName ? (
              <AppText weight="medium" className={`text-base ${textClass}`} numberOfLines={1}>
                {countryName}
              </AppText>
            ) : (
              <AppText weight="medium" className="text-base text-dark-text-15" numberOfLines={1}>
                {t(placeholder)}
              </AppText>
            )}
          </View>
          <Icon as={Feather} name="chevron-down" size={14} className="text-supporting-text" />
        </TouchableOpacity>
      </View>

      {!!hint && !error && (
        <AppText weight="medium" className="text-xs text-dark-text-10 mt-1 text-start">
          {t(hint)}
        </AppText>
      )}
      {!!error && (
        <AppText role="alert" className="text-xs text-error mt-1 text-start">
          {t(error)}
        </AppText>
      )}

      {/* ── Tablet dropdown ── */}
      {isWide && (
        <Modal
          visible={dropdownOpen}
          transparent
          animationType="none"
          onRequestClose={() => {
            setDropdownOpen(false);
            setSearchQuery("");
          }}
          statusBarTranslucent
        >
          <View style={{ flex: 1 }}>
            {/* Backdrop — taps outside the panel close it */}
            <Pressable
              testID="country-picker-backdrop"
              style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
              onPress={() => {
                setDropdownOpen(false);
                setSearchQuery("");
              }}
            />
            {/* Panel rendered after backdrop → higher z-order → absorbs all taps */}
            {dropdownPos && (
              <View
                style={{
                  position: "absolute",
                  top: _flipUp
                    ? dropdownPos.triggerTop - _panelMaxH - 4
                    : dropdownPos.triggerBottom + 4,
                  bottom: _flipUp ? windowHeight - dropdownPos.triggerTop + 4 : undefined,
                  left: dropdownPos.left,
                  width: dropdownPos.width,
                  zIndex: 1,
                  backgroundColor: "white",
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: "#E5E7EB",
                  shadowColor: "#000",
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.08,
                  shadowRadius: 8,
                  elevation: 8,
                  overflow: "hidden",
                  maxHeight: _panelMaxH,
                  minHeight: 200,
                }}
              >
                <View className="px-3 pt-3 pb-2">
                  <SearchInput
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                    placeholder="common.searchCountriesPlaceholder"
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                </View>
                <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator>
                  {filteredCountries.map((country) => {
                    const isSelected = selected?.name === country.name;
                    return (
                      <Pressable
                        key={`${country.dialCode}-${country.name}`}
                        onPress={() => {
                          setDropdownOpen(false);
                          setSearchQuery("");
                          onCountrySelect?.(country);
                        }}
                        accessibilityRole="button"
                        accessibilityLabel={country.name}
                        accessibilityState={{ selected: isSelected }}
                        className={`flex-row items-center px-4 py-3 gap-3 border-b border-stroke-50 ${
                          isSelected ? "bg-blue-5" : ""
                        }`}
                      >
                        <AppText className="text-2xl">{country.flag}</AppText>
                        <AppText
                          weight={isSelected ? "semibold" : "regular"}
                          className="text-base text-dark-text-50"
                          numberOfLines={1}
                        >
                          {country.name}
                        </AppText>
                      </Pressable>
                    );
                  })}
                  {filteredCountries.length === 0 && (
                    <EmptyState title="multiSelectPicker.noResults" />
                  )}
                </ScrollView>
              </View>
            )}
          </View>
        </Modal>
      )}

      {/* ── Mobile bottom sheet ── */}
      <CountryPickerSheet
        ref={actionSheetRef}
        selected={selected}
        onSelect={(country) => {
          close();
          onCountrySelect?.(country);
        }}
      />
    </View>
  );
});
